/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useCallback, useContext, useMemo } from 'react';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import type { DSControlledDateTimePickerT } from '../../../react-desc-prop-types.js';
import { convertToPositiveNumberIfPossible } from '../../../utils/numberHelpers.js';
import { deconstructValuesFromTimeString, prependStringWithPlaceHolders } from '../../../utils/stringHelpers.js';
import { useTimeInputs } from '../../TimeInputs/useTimeInputs.js';

const totalHoursList = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const hoursInterval = 1; // using this just to unify the logic between how hours and minutes are handled
const hoursStartPositionOffset = 1; // if array start from 01, this is 1, if start from 02, it's 2 ...

const getTotalMinuteListWithStep = (step = 1) => {
  const length = Math.ceil(60 / step);
  return new Array(length).fill('_').map((_v, i) => prependStringWithPlaceHolders(`${i * step}`, 2));
};
interface UseCurrentDisplayedWheelsLogic {
  currHourNum: number;
  visibleHours: (string | null)[];
  handlePrevHour: (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => void;
  handleNextHour: (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => void;
  handleTimeWheelBtnChangeHours: (newVal: string, e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => void;
  currMinuteNum: number;
  visibleMinutes: (string | null)[];
  handlePrevMinute: (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => void;
  handleNextMinute: (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => void;
  handleTimeWheelBtnChangeMinutes: (newVal: string, e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => void;
  handleTimeWheelBtnChangeMeridiem: (
    newMeridiem: string,
    e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent,
  ) => void;
  currMeridiem: string;
}
export const useCurrentDisplayedWheelsLogic = (): UseCurrentDisplayedWheelsLogic => {
  const {
    minutesInterval,
    onTimeWheelOpenStartingTime,
    hours,
    minutes,
    meridiem,
    props: { readOnly, applyAriaDisabled, disabled },
  } = useContext(ControlledDateTimePickerContext);
  const { handleChangeHours, handleChangeMinutes, handleChangeMeridiem } = useTimeInputs();
  const { startingHours, startingMinutes, startingMeridiem } = useMemo(() => {
    const startingVals = deconstructValuesFromTimeString(onTimeWheelOpenStartingTime);
    return {
      startingHours: startingVals.hours,
      startingMinutes: startingVals.minutes,
      startingMeridiem: startingVals.meridiem,
    };
  }, [onTimeWheelOpenStartingTime]);

  const currHourNum = useMemo(() => {
    const hoursAsNum = convertToPositiveNumberIfPossible(hours);
    if (hours && hoursAsNum !== -1) {
      return hoursAsNum;
    }
    const startingHoursAsNum = convertToPositiveNumberIfPossible(startingHours);
    if (startingHoursAsNum === -1) return 1;
    return startingHoursAsNum;
  }, [hours, startingHours]);

  const currMinuteNum = useMemo(() => {
    const minutesAsNum = convertToPositiveNumberIfPossible(minutes);
    if (minutes && minutesAsNum !== -1) {
      return minutesAsNum;
    }
    const startingMinutesAsNum = convertToPositiveNumberIfPossible(startingMinutes);
    if (startingMinutesAsNum === -1) return 0;
    return startingMinutesAsNum;
  }, [minutes, startingMinutes]);

  const currMeridiem = useMemo(() => {
    if (meridiem !== '') return meridiem;
    if (startingMeridiem !== '') return startingMeridiem;
    return 'AM';
  }, [meridiem, startingMeridiem]);

  const totalMinutesList = useMemo(() => getTotalMinuteListWithStep(minutesInterval), [minutesInterval]);
  const currMinuteIndex = useMemo(() => Math.floor(currMinuteNum / minutesInterval), [currMinuteNum, minutesInterval]);
  const currHoursIndex = useMemo(
    () => Math.floor(currHourNum / hoursInterval) - hoursStartPositionOffset,
    [currHourNum],
  );

  const visibleHours = useMemo(() => {
    const newHours = [];
    if (currHoursIndex >= 1) newHours.push(totalHoursList[currHoursIndex - 1]);
    else newHours.push(null);
    newHours.push(totalHoursList[currHoursIndex]);
    if (currHoursIndex <= 11) newHours.push(totalHoursList[currHoursIndex + 1]);
    else newHours.push(null);

    return newHours;
  }, [currHoursIndex]);
  const visibleMinutes = useMemo(() => {
    const newMinutes = [];

    if (currMinuteIndex >= 1) newMinutes.push(totalMinutesList[currMinuteIndex - 1]);
    else newMinutes.push(null);
    newMinutes.push(totalMinutesList[currMinuteIndex]);
    if (currMinuteIndex < totalMinutesList.length) newMinutes.push(totalMinutesList[currMinuteIndex + 1]);
    else newMinutes.push(null);
    return newMinutes;
  }, [currMinuteIndex, totalMinutesList]);

  const handleTimeWheelBtnChangeHours = useCallback(
    (newHours: string, e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      if (readOnly || applyAriaDisabled || disabled) return;
      handleChangeHours(newHours, e);
    },
    [readOnly, applyAriaDisabled, disabled, handleChangeHours],
  );
  const handleChangeCurrHour = useCallback(
    (offset: number, e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      const newH = currHourNum + offset;
      if (newH > 0 && newH <= 12) handleChangeHours(prependStringWithPlaceHolders(`${newH}`, 2), e);
    },
    [currHourNum, handleChangeHours],
  );
  const handlePrevHour = useCallback(
    (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      if (readOnly || applyAriaDisabled || disabled) return;
      handleChangeCurrHour(-1, e);
    },
    [readOnly, applyAriaDisabled, disabled, handleChangeCurrHour],
  );
  const handleNextHour = useCallback(
    (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      if (readOnly || applyAriaDisabled || disabled) return;
      handleChangeCurrHour(1, e);
    },
    [readOnly, applyAriaDisabled, disabled, handleChangeCurrHour],
  );

  const handleTimeWheelBtnChangeMinutes = useCallback(
    (newMinutes: string, e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      if (readOnly || applyAriaDisabled || disabled) return;
      handleChangeMinutes(newMinutes, e);
    },
    [readOnly, applyAriaDisabled, disabled, handleChangeMinutes],
  );
  const handleChangeCurrMinute = useCallback(
    (offset: number, e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      const newM = currMinuteNum + offset;
      if (newM >= 0 && newM <= 59) handleChangeMinutes(prependStringWithPlaceHolders(`${newM}`, 2), e);
    },
    [currMinuteNum, handleChangeMinutes],
  );
  const handlePrevMinute = useCallback(
    (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      if (readOnly || applyAriaDisabled || disabled) return;
      handleChangeCurrMinute(-minutesInterval, e);
    },
    [readOnly, applyAriaDisabled, disabled, handleChangeCurrMinute, minutesInterval],
  );
  const handleNextMinute = useCallback(
    (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      if (readOnly || applyAriaDisabled || disabled) return;
      handleChangeCurrMinute(minutesInterval, e);
    },
    [readOnly, applyAriaDisabled, disabled, handleChangeCurrMinute, minutesInterval],
  );

  const handleTimeWheelBtnChangeMeridiem = useCallback(
    (newMeridiem: string, e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      if (readOnly || applyAriaDisabled || disabled) return;
      handleChangeMeridiem(newMeridiem, e);
    },
    [readOnly, applyAriaDisabled, disabled, handleChangeMeridiem],
  );

  return useMemo(
    () => ({
      currHourNum,
      visibleHours,
      handlePrevHour,
      handleNextHour,
      handleTimeWheelBtnChangeHours,
      currMinuteNum,
      visibleMinutes,
      handlePrevMinute,
      handleNextMinute,
      handleTimeWheelBtnChangeMinutes,
      handleTimeWheelBtnChangeMeridiem,
      currMeridiem,
    }),
    [
      currHourNum,
      currMinuteNum,
      currMeridiem,
      handleNextHour,
      handleNextMinute,
      handlePrevHour,
      handlePrevMinute,
      handleTimeWheelBtnChangeHours,
      handleTimeWheelBtnChangeMeridiem,
      handleTimeWheelBtnChangeMinutes,
      visibleHours,
      visibleMinutes,
    ],
  );
};
